import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface ReviewSummaryBarProps {
  totalQuestions: number;
  exactMatches: number;
  suggestedMatches: number;
  generatedResponses: number;
  noMatches: number;
  reviewed: number;
  className?: string;
}

export function ReviewSummaryBar({
  totalQuestions,
  exactMatches,
  suggestedMatches,
  generatedResponses,
  noMatches,
  reviewed,
  className,
}: ReviewSummaryBarProps) {
  const readyToUse = exactMatches;
  const toReview = suggestedMatches + generatedResponses;
  const needsInput = noMatches;

  // Progress bar segments
  const segments = [
    { count: exactMatches, className: 'bg-tier-exact', label: 'Matched' },
    { count: suggestedMatches, className: 'bg-tier-suggested', label: 'Close Match' },
    { count: generatedResponses, className: 'bg-tier-generated', label: 'AI Draft' },
    { count: noMatches, className: 'bg-tier-nomatch', label: 'Needs Answer' },
  ].filter((s) => s.count > 0);

  return (
    <Card className={className}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm">
            <span className="font-semibold">{totalQuestions}</span> questions analyzed:
            {readyToUse > 0 && (
              <>
                {' '}<span className="font-medium text-tier-exact">{readyToUse}</span> ready to use
              </>
            )}
            {toReview > 0 && (
              <>
                , <span className="font-medium text-tier-generated">{toReview}</span> to review
              </>
            )}
            {needsInput > 0 && (
              <>
                , <span className="font-medium text-tier-nomatch">{needsInput}</span> need your input
              </>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {reviewed} of {totalQuestions} reviewed
          </p>
        </div>

        {/* Stacked progress bar. The segments are distinguished only by color,
            so the bar carries a text summary via role="img" + aria-label for
            keyboard and screen-reader users; the per-segment `title` is a
            sighted-only hover affordance. */}
        <div
          role="img"
          aria-label={
            `Breakdown of ${totalQuestions} questions: ` +
            segments
              .map(
                (s) =>
                  `${s.count} ${s.label} (${((s.count / totalQuestions) * 100).toFixed(0)}%)`,
              )
              .join(', ')
          }
          className="flex h-2.5 rounded-full overflow-hidden bg-muted"
        >
          {segments.map((segment) => (
            <div
              key={segment.label}
              aria-hidden="true"
              className={cn(segment.className, 'transition-all')}
              style={{ width: `${(segment.count / totalQuestions) * 100}%` }}
              title={`${segment.label}: ${segment.count} (${((segment.count / totalQuestions) * 100).toFixed(0)}%)`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
